/**
 * ImageProviderSelector — thin wrapper around ProviderPicker.
 *
 * Maps AI_IMAGE_PROVIDERS constants to PickerOption[], applies NSFW geo-gate
 * and per-provider availability (routing_mode / is_local / is_installed, from
 * the shared GET /api/v1/video-providers/image-availability contract — same
 * hook used by VideoConfigForm's I2V panel) at the mapping layer (before
 * ProviderPicker), then delegates all sorting / search / collapsed-preview
 * logic to the shared ProviderPicker primitive.
 */

import { memo, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { AI_IMAGE_PROVIDERS, type AIImageProvider } from '../../constants/imageSource';
import { useGeoRestriction } from '../../hooks/useGeoRestriction';
import { FreeOnDesktopBadge } from '../common/FreeOnDesktopBadge';
import { getEffectivePricing, isLocalPricingActive } from '../../utils/localPricing';
import { useImageProviderAvailability } from '../../hooks/useImageProviderAvailability';
import { gateLocalComponent } from '../../utils/localComponentGate';
import { useToast } from '../../hooks/useToast';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';

/**
 * Fallback ONLY for the brief window before the availability endpoint
 * resolves (imageAvailability === null): which providers have a local
 * execution path at all. Once availability loads, `avail.routing_mode`
 * (the real, backend-declared signal) is used instead — this set never
 * drives pricing or selectability on its own.
 */
const LOCAL_CAPABLE_IMAGE_PROVIDERS = new Set<AIImageProvider>(['fooocus', 'comfyui-sd']);

interface ImageProviderSelectorProps {
  value: AIImageProvider;
  onChange: (provider: AIImageProvider) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * ImageProviderSelector - collapsible provider grid for AI image providers.
 * Replaces the old inline sort + card-grid with the shared ProviderPicker.
 */
export const ImageProviderSelector = memo(function ImageProviderSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: ImageProviderSelectorProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isFeatureAllowed } = useGeoRestriction();
  // Single source of truth for BOTH local-only (ComfyUI-SD) and hybrid
  // (Fooocus -> fooocus_runpod on RunPod) providers: shared/routes/
  // video_providers.py `_build_image_response()`. Replaces the old
  // per-provider useComponentStatus('fooocus'/'comfyui') calls, which could
  // only see "installed locally" and had no notion of a hybrid provider's
  // cloud fallback — that gap made this component (a) show "Free" for
  // Fooocus regardless of whether it was actually installed (billed via
  // RunPod when it wasn't) and (b) disable the Fooocus card as "not
  // installed" even when it was perfectly usable via the cloud fallback.
  const { availability: imageAvailability } = useImageProviderAvailability();

  const notInstalledTooltip = useMemo(
    () =>
      t('imageSource.errors.componentNotInstalled', {
        defaultValue: 'Not installed. Install it in Settings > Additional Installations to use it.',
      }),
    [t]
  );
  const cloudNotConfiguredTooltip = useMemo(
    () =>
      t('imageSource.errors.cloudNotConfigured', {
        defaultValue: 'This provider is temporarily unavailable. Please try again later.',
      }),
    [t]
  );

  const options = useMemo<PickerOption<{ isLocal: boolean }>[]>(() => {
    const nsfwAllowed = isFeatureAllowed('nsfw_models');

    return AI_IMAGE_PROVIDERS.filter((p) => p.enabled && (nsfwAllowed || !p.nsfw)).map((p) => {
      const avail = imageAvailability?.get(p.value) ?? null;
      // Capability ("this provider type has a local execution path"): a
      // static, platform-independent fact taken from the backend's
      // routing_mode metadata once loaded (identical on web and desktop —
      // NOT affected by install state), falling back to the static set only
      // while availability hasn't resolved yet. Drives the FreeOnDesktopBadge
      // (web marketing) and the local/cloud discount eligibility.
      const isLocal =
        avail !== null
          ? avail.routing_mode !== 'cloud_only'
          : LOCAL_CAPABLE_IMAGE_PROVIDERS.has(p.value as AIImageProvider);
      // Real, install-state-dependent signal — conservative (false) until
      // confirmed, so the UI never shows "Free" before the backend confirms
      // the local component is actually installed.
      const installed = avail?.is_installed ?? false;
      const effectivePricing = getEffectivePricing(p.pricing, isLocal, { installed });
      const localPricingActive = isLocalPricingActive(isLocal, installed);

      // Selectability is routing_mode-aware and reuses the backend's own
      // unavailable_reason per branch (mirrors AIVideoProviderPanel's
      // hasCloudFallback logic) instead of re-deriving install state here:
      //   - hybrid (Fooocus): a missing local install is FINE — the provider
      //     still routes to Cloud Run (fooocus_runpod). Disable ONLY when
      //     NEITHER path works, i.e. the cloud endpoint itself is not
      //     provisioned on this deployment (unavailable_reason ===
      //     'cloud_not_configured'). Disabling on "not installed" here was a
      //     second bug in this file: Fooocus-without-a-local-install is
      //     perfectly usable via RunPod and must stay selectable.
      //   - local_only (ComfyUI-SD): no cloud fallback exists, so it MUST be
      //     installed. It auto-starts on demand and is VRAM-managed, so
      //     "installed" (running OR idle) is enough — never require
      //     "already running" (gateLocalComponent, the shared rule).
      //   - cloud_only, or availability not loaded yet: always selectable.
      let unavailable = false;
      let disabledReasonText: string | undefined;
      if (avail !== null) {
        if (avail.routing_mode === 'hybrid') {
          unavailable = avail.unavailable_reason === 'cloud_not_configured';
          disabledReasonText = unavailable ? cloudNotConfiguredTooltip : undefined;
        } else if (avail.routing_mode === 'local_only') {
          unavailable = !gateLocalComponent(avail).selectable;
          disabledReasonText = unavailable ? notInstalledTooltip : undefined;
        }
      }

      return {
        value: p.value,
        label: t(p.labelKey, { defaultValue: p.value }),
        description: p.descriptionKey ? t(p.descriptionKey, { defaultValue: '' }) : undefined,
        pricing: effectivePricing,
        pricingNumeric: localPricingActive ? 0 : undefined,
        speed: p.speed,
        quality: p.quality,
        recommended: p.recommended === true,
        nsfw: p.nsfw,
        badge:
          p.badge === 'LOCAL'
            ? { text: 'LOCAL', tone: 'local' as const }
            : p.badge === '4K'
              ? { text: '4K', tone: '4k' as const }
              : undefined,
        disabled: unavailable,
        disabledReason: disabledReasonText,
        meta: { isLocal },
      } satisfies PickerOption<{ isLocal: boolean }>;
    });
  }, [isFeatureAllowed, imageAvailability, notInstalledTooltip, cloudNotConfiguredTooltip, t]);

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 flex-shrink-0" />
        <span>{t('image.provider.label', { defaultValue: 'AI Provider' })}</span>
      </label>

      <ProviderPicker<{ isLocal: boolean }>
        options={options}
        value={value}
        onChange={(v) => onChange(v as AIImageProvider)}
        sortKeys={['price', 'speed', 'quality', 'name']}
        defaultSortKey="price"
        disabled={disabled}
        onDisabledClick={(opt) =>
          showToast('warning', opt.label, opt.disabledReason ?? notInstalledTooltip, 6000)
        }
        renderCardExtras={(opt) =>
          opt.meta?.isLocal ? <FreeOnDesktopBadge className="justify-center mt-1" /> : null
        }
      />
    </div>
  );
});

export default ImageProviderSelector;
